import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Grid,
  Paper,
  Typography,
  Stack,
  GlobalStyles,
} from "@mui/material";
import ColorUtils from "../utils/colors";
import logo256 from "../assets/logo_256.png";
import phonePage from "../assets/PhonePage.png";
import webPage from "../assets/WebPage.png";
import localization from "../utils/localization";
import KeyFeature from "../components/KeyFeature";
import ServerUtils from "../utils/server";
import toast from "react-hot-toast";
import IndexCard from "../components/IndexCard";
import { useNavigate } from "react-router-dom";

export function Index(props) {
  const [pwdList, setPwdList] = useState([]);
  const [isScrolled, setIsScrolled] = useState(false);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      setIsScrolled(container.scrollTop > 20);
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  function refreshPwdList() {
    ServerUtils.listNewest().then(async (resp) => {
      if (!resp.ok) {
        toast.error(localization.strings.networkErr);
        return;
      }
      let respJson = await resp.json();
      if (respJson.errno !== 0) {
        toast.error(ServerUtils.handleError(respJson));
        return;
      }
      setPwdList(respJson.data.slice(0, 3));
    });
  }

  useEffect(() => {
    refreshPwdList();
  }, []);

  return (
    <Box
      ref={containerRef}
      sx={{
        backgroundColor: ColorUtils.white,
        width: "100%",
        height: "100%",
        overflowY: "scroll",
        overflowX: "hidden",
      }}
    >
      <GlobalStyles
        styles={{
          "@keyframes float": {
            "0%, 100%": { transform: "translateY(0)" },
            "50%": { transform: "translateY(-20px)" },
          },
          "@keyframes float-delay": {
            "0%, 100%": { transform: "translateY(0)" },
            "50%": { transform: "translateY(-15px)" },
          },
        }}
      />
      {/* Enhanced Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1000,
          backgroundColor: isScrolled ? "rgba(255, 255, 255, 0.98)" : "transparent",
          backdropFilter: isScrolled ? "blur(12px)" : "none",
          transition: "all 0.3s ease",
          borderBottom: isScrolled ? "1px solid rgba(0, 0, 0, 0.05)" : "1px solid transparent",
          boxShadow: isScrolled ? "0 4px 20px rgba(0, 0, 0, 0.03)" : "none",
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction="row"
            alignItems="center"
            sx={{ height: "4.5rem" }}
          >
            <Stack
              direction="row"
              alignItems="center"
              sx={{ cursor: "pointer" }}
              onClick={() => {
                containerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              <Box
                sx={{
                  p: "1.5px",
                  borderRadius: "14px",
                  boxShadow: "0 10px 25px rgba(127, 127, 213, 0.25), inset 0 1px 1px rgba(255, 255, 255, 0.6)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="img"
                  src={logo256}
                  alt="EasyPass Logo"
                  sx={{
                    display: "block",
                    height: "2.6rem",
                    width: "2.6rem",
                    borderRadius: "12px",
                    backgroundColor: "#fff",
                    p: "1px",
                  }}
                />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: ColorUtils.textColor,
                  ml: 2,
                  letterSpacing: "-0.02em",
                  fontSize: "1.4rem",
                }}
              >
                EasyPass
              </Typography>
            </Stack>

            <Box sx={{ flex: 1 }} />

            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="text"
                sx={{
                  color: ColorUtils.textColorGrey,
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { color: ColorUtils.blue, backgroundColor: "transparent" }
                }}
                onClick={() => {
                  navigate(`/login.html?lang=${localization.language.lang}`);
                }}
              >
                {localization.strings.login}
              </Button>

              <Button
                variant="contained"
                disableElevation
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                  backgroundColor: ColorUtils.blue,
                  "&:hover": { backgroundColor: ColorUtils.primaryDark }
                }}
                onClick={() => {
                  navigate(`/login.html?lang=${localization.language.lang}`);
                }}
              >
                {localization.strings.register}
              </Button>

              <Box sx={{ width: "1px", height: "1.5rem", backgroundColor: "rgba(0,0,0,0.1)", mx: 1 }} />

              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select
                  value={localization.language.lang || "en"}
                  onChange={(event) => {
                    window.location.href = "?lang=" + event.target.value;
                  }}
                  size="small"
                  sx={{
                    color: ColorUtils.textColor,
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    backgroundColor: "rgba(0,0,0,0.03)",
                    borderRadius: "10px",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                  }}
                >
                  <MenuItem value="en">EN</MenuItem>
                  <MenuItem value="zh">ZH</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg">
        <Grid container spacing={4} sx={{ py: 6 }}>
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              minHeight: "30rem",
            }}
          >
            <Typography
              variant="h2"
              component="h1"
              sx={{
                color: ColorUtils.textColor,
                fontWeight: 800,
                lineHeight: 1.2,
                mb: 2,
                fontSize: { xs: "2.5rem", md: "3.5rem" },
              }}
            >
              <Box component="span" sx={{ color: ColorUtils.red }}>
                {localization.strings.secure}
              </Box>
              {localization.strings.indexTitle}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: ColorUtils.textColorGrey,
                fontSize: "1.1rem",
                lineHeight: 1.6,
                mb: 4,
                maxWidth: "500px",
              }}
            >
              {localization.strings.indexTitleDesc}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                "& .MuiButton-root": {
                  borderRadius: "12px",
                  px: 3,
                  py: 1,
                  textTransform: "none",
                  fontWeight: 600,
                  boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
                  transition: "all 0.3s ease",
                },
                "& .MuiButton-root:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: "0 10px 20px rgba(0,0,0,0.15)",
                },
              }}
            >
              <Button
                variant="contained"
                color="success"
                href="https://apps.apple.com/cn/app/easypass%E5%AF%86%E7%A0%81%E7%AE%A1%E7%90%86%E5%B7%A5%E5%85%B7/id1534324173"
                target="_blank"
                startIcon={<i className="iconfont icon-ios" style={{ fontSize: "1.4rem" }} />}
              >
                AppStore
              </Button>
              <Button
                variant="contained"
                color="primary"
                href="https://play.google.com/store/apps/details?id=com.musketeer.easypass"
                target="_blank"
                startIcon={<i className="iconfont icon-google-play" style={{ fontSize: "1.4rem" }} />}
              >
                GooglePlay
              </Button>
              <Button
                variant="contained"
                color="info"
                href="https://easypass-tech.oss-cn-hangzhou.aliyuncs.com/app-release.apk"
                target="_blank"
                startIcon={<i className="iconfont icon-android-fill" style={{ fontSize: "1.4rem" }} />}
              >
                Android APK
              </Button>
              <Button
                variant="contained"
                color="warning"
                href={`https://chromewebstore.google.com/detail/easypass/hepcelaldempfnmhkdehiggikjdkiahh`}
                target="_blank"
                startIcon={<i className="iconfont icon-extension" style={{ fontSize: "1.4rem" }} />}
              >
                {localization.strings.extension}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                href={`/login.html?lang=${localization.language.lang}`}
                target="_blank"
                startIcon={<i className="iconfont icon-chrome" style={{ fontSize: "1.4rem" }} />}
              >
                Web UI
              </Button>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              position: "relative",
              display: { xs: "none", md: "block" },
              zIndex: 1, // 创建层叠上下文
            }}
          >
            <Box
              component="img"
              src={phonePage}
              sx={{
                display: "block",
                height: "30rem",
                mt: 10,
                filter: "drop-shadow(0 20px 40px rgba(0,0,0,0.15))",
                animation: "float 6s ease-in-out infinite",
                position: "relative",
                zIndex: 2, // 确保在背景图之上
              }}
            />
            <Box
              component="img"
              src={webPage}
              sx={{
                position: "absolute",
                top: 0,
                left: "6rem",
                display: "block",
                height: "35rem",
                zIndex: 1, // 从 -1 改为 1，确保不被父容器背景遮挡
                filter: "drop-shadow(0 25px 50px rgba(0,0,0,0.12))",
                animation: "float-delay 8s ease-in-out infinite",
              }}
            />
          </Grid>
        </Grid>
      </Container>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          align="center"
          sx={{ color: ColorUtils.textColor, fontWeight: 700, mb: 1 }}
        >
          {localization.strings.indexSection1Title}
        </Typography>
        <Typography
          variant="subtitle1"
          align="center"
          sx={{ color: ColorUtils.textColorGrey, mb: 6 }}
        >
          {localization.strings.indexSection1SubTitle}
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={4}>
            <KeyFeature
              icon="icon-check"
              iconColor={ColorUtils.indexGreen}
              iconColorBg={ColorUtils.indexGreenBg}
              title={localization.strings.indexKeyFeature1Title}
              desc={localization.strings.indexKeyFeature1Desc}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <KeyFeature
              icon="icon-cloud"
              iconColor={ColorUtils.indexBlue}
              iconColorBg={ColorUtils.indexBlueBg}
              title={localization.strings.indexKeyFeature2Title}
              desc={localization.strings.indexKeyFeature2Desc}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <KeyFeature
              icon="icon-lock"
              iconColor={ColorUtils.indexRed}
              iconColorBg={ColorUtils.indexRedBg}
              title={localization.strings.indexKeyFeature3Title}
              desc={localization.strings.indexKeyFeature3Desc}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} sx={{ ml: { md: "16.66%", sm: 0 } }}>
            <KeyFeature
              icon="icon-bitcoin"
              iconColor={ColorUtils.indexOrange}
              iconColorBg={ColorUtils.indexOrangeBg}
              title={localization.strings.indexKeyFeature4Title}
              desc={localization.strings.indexKeyFeature4Desc}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <KeyFeature
              icon="icon-envelope"
              iconColor={ColorUtils.indexPurple}
              iconColorBg={ColorUtils.indexPurpleBg}
              title={localization.strings.indexKeyFeature5Title}
              desc={localization.strings.indexKeyFeature5Desc}
            />
          </Grid>
        </Grid>
      </Container>

      <Box sx={{ backgroundColor: ColorUtils.lightGrey, py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            sx={{ color: ColorUtils.textColor, fontWeight: 700, mb: 1 }}
          >
            {localization.strings.indexSection2Title}
          </Typography>
          <Typography
            variant="subtitle1"
            align="center"
            sx={{ color: ColorUtils.textColorGrey, mb: 3 }}
          >
            {localization.strings.indexSection2SubTitle}
          </Typography>
          <Button
            variant="contained"
            color="info"
            sx={{
              mx: "auto",
              display: "block",
              mb: 5,
              textTransform: "none",
              borderRadius: "8px",
            }}
            onClick={refreshPwdList}
          >
            {localization.strings.refresh}
          </Button>
          <Grid container spacing={3}>
            {pwdList.map((item, index) => (
              <Grid key={index} item xs={12} md={4}>
                <IndexCard pwd={item} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography
          variant="h3"
          align="center"
          sx={{ color: ColorUtils.textColor, fontWeight: 700, mb: 6 }}
        >
          {localization.strings.pricing}
        </Typography>
        <Grid container spacing={4} justifyContent="center">
          {[
            {
              title: localization.strings.freeUser,
              features: [
                { text: localization.strings.editPassword, ok: true },
                { text: localization.strings.editText, ok: true },
                { text: localization.strings.editTotp, ok: true },
                { text: localization.strings.editBlockchain, ok: true },
                { text: localization.strings.editMessage, ok: true },
                { text: localization.strings.editTag, ok: false },
                { text: localization.strings.addExtraKeys, ok: false },
                { text: localization.strings.tagAddContent, ok: false },
                { text: localization.strings.autocomplete, ok: false },
              ],
              buttonText: localization.strings.newUserVipTime,
            },
            {
              title: localization.strings.vipUser,
              features: [
                { text: localization.strings.editPassword, ok: true },
                { text: localization.strings.editText, ok: true },
                { text: localization.strings.editTotp, ok: true },
                { text: localization.strings.editBlockchain, ok: true },
                { text: localization.strings.editMessage, ok: true },
                { text: localization.strings.editTag, ok: true },
                { text: localization.strings.addExtraKeys, ok: true },
                { text: localization.strings.tagAddContent, ok: true },
                { text: localization.strings.autocomplete, ok: true },
              ],
              buttonText: localization.strings.vipPayInfo,
              highlight: true,
            },
          ].map((plan, idx) => (
            <Grid item key={idx} xs={12} sm={6} md={5}>
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: ColorUtils.white,
                  p: 4,
                  borderRadius: "24px",
                  border: plan.highlight ? `2px solid ${ColorUtils.blue}20` : "1px solid rgba(0,0,0,0.06)",
                  boxShadow: "0 10px 40px rgba(0,0,0,0.06)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "0 20px 50px rgba(0,0,0,0.12)",
                  },
                }}
              >
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: ColorUtils.textColor }}>
                  {plan.title}
                </Typography>
                <Stack spacing={1.5} sx={{ mb: 4 }}>
                  {plan.features.map((feat, fIdx) => (
                    <Stack key={fIdx} direction="row" alignItems="center" spacing={1}>
                      <i
                        className={`iconfont ${feat.ok ? "icon-check-" : "icon-14CIRCLE"}`}
                        style={{
                          fontSize: "1rem",
                          color: feat.ok ? ColorUtils.green : ColorUtils.red,
                        }}
                      />
                      <Typography variant="body2" sx={{ color: ColorUtils.textColor }}>
                        {feat.text}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
                <Button
                  fullWidth
                  variant={plan.highlight ? "contained" : "outlined"}
                  color="info"
                  sx={{ textTransform: "none", borderRadius: "10px", py: 1 }}
                >
                  {plan.buttonText}
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Box sx={{ backgroundColor: ColorUtils.lightGrey, py: 4, borderTop: "1px solid rgba(0,0,0,0.05)" }}>
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Typography variant="body2" sx={{ color: ColorUtils.textColor }}>
              Copyright © 2020-2024 EasyPass. All rights reserved.{" "}
              <Button
                href="https://beian.miit.gov.cn/"
                target="_blank"
                sx={{ textTransform: "none", ml: 1, minWidth: "auto", p: 0 }}
              >
                浙ICP备18002047号-2
              </Button>
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <a
                href="mailto:easypass.inc@gmail.com"
                style={{
                  color: ColorUtils.textColor,
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <i className="iconfont icon-envelope" style={{ marginRight: "0.5rem" }} />
                <Typography variant="body2">easypass.inc@gmail.com</Typography>
              </a>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}

export default Index;
